import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { NotificationGlobalService } from './notification-global.service';

type NotificationItem = {
	readonly Notification_Id: number;
	readonly Is_Read: boolean;
	readonly [key: string]: unknown;
};

@Injectable({
	providedIn: 'root'
})
export class NotificationStoreService {
	private currentPageLoaded = 0;
	private settingData;

	private readonly _notificationsSource = new BehaviorSubject<NotificationItem[]>([]);

	readonly notifications$ = this._notificationsSource.asObservable();

	constructor(
		private http: HttpClient,
		private notificationGlobalService: NotificationGlobalService
	) {
		this.observeNewNotifications();
	}

	private observeNewNotifications() {
		this.notificationGlobalService.receiveInternalNotification$.subscribe((rs) => {
			this.reloadCurrentPage();
		});
	}

	private reloadCurrentPage() {
		const pageSize = Math.max(10, 10 * this.currentPageLoaded);
		this.getNotificationsPaging(1, pageSize).subscribe((rs) => {
			const noties = rs['Data']['$values'] as NotificationItem[];
			this._setNotifications(noties);
		});
	}

	private getNotificationById(notificationId: number): NotificationItem | null {
		return this.getNotifications().find((notification) => notification.Notification_Id == notificationId) ?? null;
	}

	private _setNotifications(noties: NotificationItem[]): void {
		this._notificationsSource.next(noties);
	}

	private adjustUnreadCount(delta: number): void {
		const current = this.notificationGlobalService.receiveUnseenNotificationsCountSource.getValue();
		this.notificationGlobalService.receiveUnseenNotificationsCountSource.next(Math.max(0, current + delta));
	}

	private setUnreadCount(count: number): void {
		this.notificationGlobalService.receiveUnseenNotificationsCountSource.next(Math.max(0, count));
	}

	getNotifications(): NotificationItem[] {
		return this._notificationsSource.getValue();
	}

	loadMore(callBack: Function = null, failedCallBack: Function = null) {
		this.getNotificationsPaging(++this.currentPageLoaded, 10).subscribe(
			(rs) => {
				const newNoties = rs['Data']['$values'] as NotificationItem[];
				const noties = [...this.getNotifications(), ...newNoties];
				this._setNotifications(noties);
				if (callBack) {
					callBack();
				}
			},
			(err) => {
				if (failedCallBack) {
					failedCallBack();
				}
			}
		);
	}

	refreshNotifications(callBack: Function = null, failedCallBack: Function = null) {
		this.currentPageLoaded = 1;
		this.getNotificationsPaging(this.currentPageLoaded, 10).subscribe(
			(rs) => {
				const noties = rs['Data']['$values'] as NotificationItem[];
				this._setNotifications(noties);
				if (callBack) {
					callBack();
				}
			},
			(err) => {
				if (failedCallBack) {
					failedCallBack();
				}
			}
		);
	}

	changeNotificationToStatus(noti: NotificationItem, status: boolean): void {
		const currentNotification = this.getNotificationById(noti.Notification_Id);
		if (!currentNotification || currentNotification.Is_Read == status) {
			return;
		}
		const previousNotifications = this.getNotifications();
		const updatedNotification = { ...currentNotification, Is_Read: status };
		const noties = previousNotifications.map((notification) =>
			notification.Notification_Id == currentNotification.Notification_Id ? updatedNotification : notification
		);
		this._setNotifications(noties);
		this.adjustUnreadCount(status ? -1 : 1);

		this.postReadStatus(updatedNotification).subscribe({
			error: () => {
				this._setNotifications(previousNotifications);
				this.adjustUnreadCount(status ? 1 : -1);
			}
		});
	}

	changeNotificationStatus(noti: NotificationItem): void {
		const currentNotification = this.getNotificationById(noti.Notification_Id);
		if (!currentNotification) {
			return;
		}
		const previousNotifications = this.getNotifications();
		const updatedNotification = { ...currentNotification, Is_Read: !currentNotification.Is_Read };
		const noties = previousNotifications.map((notification) =>
			notification.Notification_Id == currentNotification.Notification_Id ? updatedNotification : notification
		);
		const wasUnread = !currentNotification.Is_Read;
		this._setNotifications(noties);
		this.adjustUnreadCount(wasUnread ? -1 : 1);

		this.postReadStatus(updatedNotification).subscribe({
			error: () => {
				this._setNotifications(previousNotifications);
				this.adjustUnreadCount(wasUnread ? 1 : -1);
			}
		});
	}

	changeAllNotificationStatus(): void {
		const previousNotifications = this.getNotifications();
		const previousCount = this.notificationGlobalService.receiveUnseenNotificationsCountSource.getValue();
		const noties = previousNotifications.map((notification) => ({
			...notification,
			Is_Read: true,
		}));
		this._setNotifications(noties);
		this.setUnreadCount(0);

		this.postReadAllStatus().subscribe({
			error: () => {
				this._setNotifications(previousNotifications);
				this.setUnreadCount(previousCount);
			}
		});
	}

	deleteNotification(noti: NotificationItem): void {
		const currentNotification = this.getNotificationById(noti.Notification_Id);
		if (!currentNotification) {
			return;
		}
		const previousNotifications = this.getNotifications();
		const wasUnread = !currentNotification.Is_Read;
		const noties = previousNotifications.filter((notification) => notification.Notification_Id != currentNotification.Notification_Id);
		this._setNotifications(noties);
		if (wasUnread) {
			this.adjustUnreadCount(-1);
		}

		this.postDeleteNotification(currentNotification).subscribe({
			next: () => {
				this.reloadCurrentPage();
			},
			error: () => {
				this._setNotifications(previousNotifications);
				if (wasUnread) {
					this.adjustUnreadCount(1);
				}
			}
		});
	}

	// API call =>>>>>>>>>>>>>>>

	private getNotificationsPaging(pageNumber, pageSize) {
		return this.http.get('/api/notification?page=' + pageNumber + '&pageSize=' + pageSize);
	}

	private postReadStatus(noti) {
		return this.http.post('/api/notification/status/single', noti);
	}

	private postReadAllStatus() {
		return this.http.post('/api/notification/status/all?markAsRead=true', {});
	}

	private postDeleteNotification(noti) {
		return this.http.post('/api/notification/delete', noti);
	}
}
